import {gql} from '@apollo/client';
import {noop} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import assert from 'assert';
import {DocumentNode} from 'graphql';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';

import {exportAuditLogs} from 'audit/AuditActions';
import {EXPORT_AUDIT_LOG_DISPLAY_STRING} from 'audit/AuditConstants';
import {pathToURL} from 'BenchlingUtil';
import {ActionOrigin} from 'constants/AnalyticsConstants';
import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible, accessibleObjs} from 'graphql-client/InaccessibleInfo';
import {makeReactContext} from 'lib/makeReactContext';
import ToastMessage from 'nav/toasts/ToastMessage';
import notifications from 'notifications';
import ErrorBadge, {ErrorBadgeError} from 'procedures/design/error-panel/ErrorBadge';
import {promoteProcedureRunPlans} from 'procedures/network/procedureRunPlanDAO';
import {
  getPowAgnosticProcedureFromFlowchartVersion,
  getPowAgnosticTemplateFlowchartVersionFromProcedureRunPlan,
} from 'procedures/utils/powAgnosticFlowchartHelpers';
import {BenchlingState} from 'redux-stores/reduceApp';
import {navigateToStudyOverviewTab} from 'studies/FileUtils';
import {
  ProcedureRunTab,
  ProcedureRunOrPlanStateSummary,
  setSelectedProcedureRun,
  setSelectedProcedureRunPlan,
  setCurrentProcedureRunTab as setCurrentProcedureRunTabAction,
  addPromotingPlanId as addPromotingPlanIdAction,
  removePromotingPlanId as removePromotingPlanIdAction,
  resetStudyState,
  setIsInEditMode as setIsInEditModeAction,
} from 'studies/redux/ProcedureRunPlanUIReduxActions';
import {
  getExperimentUnknownErrorAction,
  onSuccessAfterExperimentCreate,
  showExperimentCreationTrayTask,
} from 'studies/sidepanel/ExperimentCreationTrayTask';
import {getProcedureRunUIStringForStudyType, getProcedureTypeForStudyType} from 'studies/TypedStudyUtils';
import {StudyOverviewViewQuery} from 'studies/workspace/overview/__generated/StudyOverviewViewQuery';
import SetStudyPhaseDropdown from 'studies/workspace/overview/SetStudyPhaseDropdown';
import {FONT_PROXIMA_12} from 'styles/FontConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import * as TrayTaskActions from 'tray/TrayTaskActions';
import * as TrayTaskConstants from 'tray/TrayTaskConstants';
import Breadcrumbs, {CrumbLink} from 'ui-library/Breadcrumbs';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import * as Colors from 'ui-library/Colors';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import {CenteredSpinner} from 'ui-library/Spinner';
import copyToClipboard from 'util/clipboard/copyToClipboard';
import {getPromotingPlanIdsForStudy} from 'util/resume-persisted-tasks/ResumePersistedTasks';

import {StudyOverviewTabContentsProps} from '../utils/TypedStudyTypes';
import AddProcedureRunButton from '../utils/ui/AddProcedureRunButton';
import AnalyzeMultipleProcedureRunsButton from '../utils/ui/AnalyzeMultipleProcedureRunsButton';
import AnalyzeProcedureRunButton from '../utils/ui/AnalyzeProcedureRunButton';
import LinkToExistingUnitOperationsButton from '../utils/ui/LinkToExistingUnitOperationsButton';
import LinkToExistingUnitOperationsPlanButton from '../utils/ui/LinkToExistingUnitOperationsPlanButton';
import {
  MultiProcedureRunOnlyOverviewQuery,
  MultiProcedureRunOnlyOverviewQueryVariables,
} from './__generated/MultiProcedureRunOnlyOverviewQuery';
import {
  MultiProcedureRunPlanOverviewQuery,
  MultiProcedureRunPlanOverviewQueryVariables,
  MultiProcedureRunPlanOverviewQuery_procedureRunPlan_ProcedureRunPlanNode as ProcedureRunPlanNode,
  MultiProcedureRunPlanOverviewQuery_procedureRunPlan_ProcedureRunPlanNode_templateProcedureFlowchartVersion_ProcedureFlowchartVersionNode as TemplateProcedureFlowchartVersionNode,
  MultiProcedureRunPlanOverviewQuery_procedureRunPlan_ProcedureRunPlanNode_procedureFlowchartConfigVersionProxy_ProcedureFlowchartConfigVersionProxyNode as ProcedureFlowchartConfigVersionProxyNode,
  MultiProcedureRunPlanOverviewQuery_procedureRunPlan_ProcedureRunPlanNode_procedureFlowchartConfigVersionProxy_ProcedureFlowchartConfigVersionProxyNode_procedure_ProcedureNode as FlowchartProxyProcedureNode,
  MultiProcedureRunPlanOverviewQuery_procedureRunPlan_ProcedureRunPlanNode_templateProcedureFlowchartVersion_ProcedureFlowchartVersionNode_procedureFlowchart_ProcedureFlowchartNode_parent_ProcedureNode as TemplateFlowchartProcedureNode,
} from './__generated/MultiProcedureRunPlanOverviewQuery';
import ConfirmPromotionModal from './ConfirmPromotionModal';
import {getProcedureRunPlanName} from './ProcedureRunPlanUtils';
import StudyExecutionHeader, {StudyExecutionHeaderTabsProps} from './StudyExecutionHeader';

type StudyNode = ExtractByTypename<StudyOverviewViewQuery['study'], 'StudyNode'>;
type PowAgnosticTemplateFlowchartVersion =
  | ProcedureFlowchartConfigVersionProxyNode
  | TemplateProcedureFlowchartVersionNode;
type ProcedureFromFlowchartVersion = FlowchartProxyProcedureNode | TemplateFlowchartProcedureNode;

export {ProcedureRunTab, ProcedureRunOrPlanStateSummary} from 'studies/redux/ProcedureRunPlanUIReduxActions';

export const StudyOverviewContext = makeReactContext<{
  selectedProcedureRun: ProcedureRunOrPlanStateSummary;
  selectedProcedureRunPlan: ProcedureRunOrPlanStateSummary;
  currentProcedureRunTab: ProcedureRunTab;
  setCurrentProcedureRunTab: (procedureRunTab: ProcedureRunTab) => void;
  promotingPlanIds: Set<string>;
  addPromotingPlanId: (planId: string) => void;
  removePromotingPlanId: (planId: string) => void;
  isInEditMode: boolean;
  setIsInEditMode: (isInEditMode: boolean) => void;
  setSavePlanIfInEditMode: (fn: (() => Promise<void>) | null) => void;
  planErrors: Array<ErrorBadgeError>;
  setPlanErrors: (errors: Array<ErrorBadgeError>) => void;
}>(
  () => ({
    selectedProcedureRun: {id: null, name: null},
    selectedProcedureRunPlan: {id: null, name: null, procedureId: null},
    currentProcedureRunTab: ProcedureRunTab.OVERVIEW,
    setCurrentProcedureRunTab: noop,
    promotingPlanIds: new Set(),
    addPromotingPlanId: noop,
    removePromotingPlanId: noop,
    isInEditMode: false,
    setIsInEditMode: noop,
    setSavePlanIfInEditMode: noop,
    planErrors: [],
    setPlanErrors: noop,
  }),
  'StudyOverviewContext'
);

/*
 * Exported for testing
 */
export function studyOverviewCrumbs(props: {
  selectedProcedureRun: ProcedureRunOrPlanStateSummary;
  selectedProcedureRunPlan: ProcedureRunOrPlanStateSummary;
  studyName: string;
  setCurrentProcedureRunTab: (procedureRunTab: ProcedureRunTab) => void;
  studyId: string;
}): JSX.Element {
  return (
    <>
      <div {...getTestProps({component: 'MultiProcedureRunOverviewTabContents', element: 'breadcrumbs'})}>
        <Breadcrumbs>
          <CrumbLink
            onClick={() => {
              // resets the procedure run tab to overview when selecting a new procedure run or design when selecting a new procedure run plan
              props.setCurrentProcedureRunTab(
                props.selectedProcedureRunPlan.id != null ? ProcedureRunTab.DESIGN : ProcedureRunTab.OVERVIEW
              );
              navigateToStudyOverviewTab(props.studyId);
            }}
          >
            {props.studyName}
          </CrumbLink>
        </Breadcrumbs>
      </div>
      {props.selectedProcedureRun.name && (
        <div className={css(styles.breadcrumbsFont)}>{props.selectedProcedureRun.name}</div>
      )}
      {props.selectedProcedureRunPlan.name && (
        <div className={css(styles.breadcrumbsFont)}>{props.selectedProcedureRunPlan.name}</div>
      )}
    </>
  );
}

/*
 * Exported for testing
 */
export function procedureRunViewerTabsProps({
  currentProcedureRunTab,
  setCurrentProcedureRunTab,
  selectedProcedureRunId,
  selectedProcedureRunPlanId,
}: {
  currentProcedureRunTab: ProcedureRunTab;
  setCurrentProcedureRunTab: (procedureRunTab: ProcedureRunTab) => void;
  selectedProcedureRunId: string | null;
  selectedProcedureRunPlanId: string | null;
}): StudyExecutionHeaderTabsProps<ProcedureRunTab> {
  const headerTabsProps = [
    {tabKey: ProcedureRunTab.DESIGN, title: 'DESIGN'},
    {tabKey: ProcedureRunTab.METADATA, title: 'METADATA'},
  ];
  if (selectedProcedureRunId != null) {
    // adds the overview tab to the beginning of the tabs array
    headerTabsProps.unshift({tabKey: ProcedureRunTab.OVERVIEW, title: 'OVERVIEW'});
  }
  if (selectedProcedureRunPlanId != null || selectedProcedureRunId != null) {
    // Fall back to the first available tab if the current tab isn't valid for this view.
    // This prevents a TabContainer crash when transitioning between run/plan/listing views
    // without re-mounting (e.g., plans only have DESIGN and METADATA, not OVERVIEW).
    const activeTab = headerTabsProps.some((tab) => tab.tabKey === currentProcedureRunTab)
      ? currentProcedureRunTab
      : headerTabsProps[0].tabKey;
    const headerTabContainerProps = {
      activeTab,
      className: css(styles.tabContainer),
      headerDividerClassName: css(styles.headerDivider),
      isFullHeight: true,
      navClassName: css(styles.tabList),
      onRequestTabChange: setCurrentProcedureRunTab,
      paneClassName: css(styles.tabPanel),
      tabClassName: css(styles.tab),
    };
    return {
      headerTabContainerProps,
      headerTabsProps,
    };
  }
  return {};
}

export const MULTI_PROCEDURE_RUN_ONLY_OVERVIEW_QUERY: DocumentNode = gql`
  query MultiProcedureRunOnlyOverviewQuery($id: StrictString!) @skipAudit @engComponent(name: STUDIES) {
    procedureRun(id: $id) {
      ... on ProcedureRunNode {
        id
        name
        archiveData {
          archived
        }
        procedureRunPlan {
          ... on ProcedureRunPlanNode {
            id
          }
          ...InaccessibleInfoGraphQL
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export const MULTI_PROCEDURE_RUN_PLAN_OVERVIEW_QUERY: DocumentNode = gql`
  query MultiProcedureRunPlanOverviewQuery($id: StrictString!) @skipAudit @engComponent(name: STUDIES) {
    procedureRunPlan(id: $id) {
      ... on ProcedureRunPlanNode {
        id
        procedureRun {
          ... on ProcedureRunNode {
            id
          }
          ...InaccessibleInfoGraphQL
        }
        templateProcedureFlowchartVersion {
          ... on ProcedureFlowchartVersionNode {
            id
            procedureFlowchart {
              ... on ProcedureFlowchartNode {
                id
                parent {
                  ... on ProcedureNode {
                    id
                    name
                  }
                }
              }
            }
            migratedToFlowchartConfigVersionProxy {
              ... on ProcedureFlowchartConfigVersionProxyNode {
                id
              }
              ...InaccessibleInfoGraphQL
            }
          }
          ...InaccessibleInfoGraphQL
        }
        procedureFlowchartConfigVersionProxy {
          ... on ProcedureFlowchartConfigVersionProxyNode {
            id
            procedure {
              ... on ProcedureNode {
                id
                name
              }
            }
          }
          ...InaccessibleInfoGraphQL
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

interface ProcedureRunOrPlanViewButtonsProps {
  study: StudyNode;
  selectedProcedureRunId?: string;
  selectedProcedureRunPlanId?: string;
  isProcedureRunArchived: boolean;
  planOrRunName: string;
  onLinksSaved?: () => void;
  onOpenPromotePlanConfirmation?: () => void;
  planErrors?: Array<ErrorBadgeError>;
  isInEditMode?: boolean;
}

function CopyLinkButton({study, id}: {study: StudyNode; id: string}): JSX.Element {
  return (
    <Button
      buttonStyle={ButtonStyle.TERTIARY_GRAY}
      size={ButtonSize.MEDIUM}
      isDropdown={false}
      onClick={() => copyToClipboard(pathToURL(`${study.editUrl}?runId=${id}`))}
      icon="link"
    />
  );
}

function ExportAuditLogButton({auditLogIds}: {auditLogIds: Array<string>}): JSX.Element {
  return (
    <Tooltip text="Export audit log">
      <Button
        buttonStyle={ButtonStyle.TERTIARY_GRAY}
        icon="audit-trail"
        size={ButtonSize.MEDIUM}
        onClick={() => {
          exportAuditLogs(auditLogIds);
        }}
        aria-label={EXPORT_AUDIT_LOG_DISPLAY_STRING}
      />
    </Tooltip>
  );
}

function ProcedureRunButtons({
  study,
  procedureRunId,
  planOrRunName,
  isProcedureRunArchived,
}: {
  study: StudyNode;
  procedureRunId: string;
  planOrRunName: string;
  isProcedureRunArchived: boolean;
}): JSX.Element {
  return (
    <>
      <LinkToExistingUnitOperationsButton
        studyId={study.id}
        procedureRunId={procedureRunId}
        procedureRunName={planOrRunName}
      />
      <AnalyzeProcedureRunButton
        studyId={study.id}
        studyName={study.name}
        studyHumanId={study.humanId}
        isStudyArchived={study.archiveRecord != null}
        studyType={study.studySchema.studyType}
        procedureRunId={procedureRunId}
        procedureRunName={planOrRunName}
        isProcedureRunArchived={isProcedureRunArchived}
        procedureType={getProcedureTypeForStudyType(study.studySchema.studyType)}
        actionOrigin={ActionOrigin.STUDY_RUN_DETAILS_PAGE}
      />
    </>
  );
}

function ProcedureRunPlanButtons({
  study,
  procedureRunPlanId,
  planOrRunName,
  onLinksSaved,
  onOpenPromotePlanConfirmation,
  planErrors,
  isInEditMode,
}: {
  study: StudyNode;
  procedureRunPlanId: string;
  planOrRunName: string;
  onLinksSaved?: () => void;
  onOpenPromotePlanConfirmation?: () => void;
  planErrors?: Array<ErrorBadgeError>;
  isInEditMode?: boolean;
}): JSX.Element {
  const hasPlanErrors = planErrors != null && planErrors.length > 0;
  return (
    <>
      <LinkToExistingUnitOperationsPlanButton
        studyId={study.id}
        procedureRunPlanId={procedureRunPlanId}
        procedureRunPlanName={planOrRunName}
        onLinksSaved={onLinksSaved ?? (() => {})}
      />
      <Button
        buttonStyle={ButtonStyle.TERTIARY_BLUE}
        size={ButtonSize.MEDIUM}
        text={isInEditMode ? 'Save and create' : 'Create'}
        disabled={hasPlanErrors}
        onClick={onOpenPromotePlanConfirmation}
        dataTestAttrs={{component: 'MultiProcedureRunOverviewTabContents', element: 'createButton'}}
      />
    </>
  );
}

function getProcedureRunOrPlanViewButtons({
  study,
  selectedProcedureRunId,
  selectedProcedureRunPlanId,
  isProcedureRunArchived,
  planOrRunName,
  onLinksSaved,
  onOpenPromotePlanConfirmation,
  planErrors,
  isInEditMode,
}: ProcedureRunOrPlanViewButtonsProps): JSX.Element {
  assert(
    selectedProcedureRunId !== undefined || selectedProcedureRunPlanId !== undefined,
    'Expected either a procedure run or procedure run plan to be selected.'
  );
  assert(planOrRunName !== '', 'Expected a plan or run name to be provided.');

  const hasPlanErrors = planErrors != null && planErrors.length > 0;
  const linkId = selectedProcedureRunId ?? selectedProcedureRunPlanId!;

  // Collect IDs for audit log export — when viewing a procedure run that was
  // promoted from a plan, both IDs are included.
  const auditLogIds = [selectedProcedureRunId, selectedProcedureRunPlanId].filter(
    (id): id is string => id != null
  );

  return (
    <>
      {selectedProcedureRunPlanId != null && hasPlanErrors && (
        <ErrorBadge
          errors={planErrors}
          title={`Fix all errors to create this ${getProcedureRunUIStringForStudyType(study.studySchema.studyType)}`}
        />
      )}
      <CopyLinkButton study={study} id={linkId} />
      {selectedProcedureRunId != null && (
        <ProcedureRunButtons
          study={study}
          procedureRunId={selectedProcedureRunId}
          planOrRunName={planOrRunName}
          isProcedureRunArchived={isProcedureRunArchived}
        />
      )}
      {selectedProcedureRunId == null && selectedProcedureRunPlanId != null && (
        <ProcedureRunPlanButtons
          study={study}
          procedureRunPlanId={selectedProcedureRunPlanId}
          planOrRunName={planOrRunName}
          onLinksSaved={onLinksSaved}
          onOpenPromotePlanConfirmation={onOpenPromotePlanConfirmation}
          planErrors={planErrors}
          isInEditMode={isInEditMode}
        />
      )}
      <ExportAuditLogButton auditLogIds={auditLogIds} />
    </>
  );
}

const EMPTY_PROMOTING_PLAN_IDS: Array<string> = [];

export default function MultiProcedureRunOverviewTabContents(
  props: StudyOverviewTabContentsProps
): JSX.Element {
  const dispatch = useDispatch();
  const studyId = props.study.id;
  const defaultTab = props.runPlanId != null ? ProcedureRunTab.DESIGN : ProcedureRunTab.OVERVIEW;

  // Get Redux state for this study using stable selectors
  const reduxCurrentTab = useSelector(
    (state: BenchlingState) => state.ui.procedureRunPlans.byStudyId[studyId]?.currentProcedureRunTab
  );
  const currentProcedureRunTab = reduxCurrentTab ?? defaultTab;

  const reduxPromotingPlanIds = useSelector(
    (state: BenchlingState) =>
      state.ui.procedureRunPlans.byStudyId[studyId]?.promotingPlanIds ?? EMPTY_PROMOTING_PLAN_IDS
  );
  const isInEditMode = useSelector(
    (state: BenchlingState) => state.ui.procedureRunPlans.byStudyId[studyId]?.isInEditMode ?? false
  );

  // Merge persisted task promoting IDs with Redux state on mount only
  const hasInitializedPromotingPlanIds = useRef(false);
  useEffect(() => {
    if (!hasInitializedPromotingPlanIds.current) {
      hasInitializedPromotingPlanIds.current = true;
      const persistedPlanIds = getPromotingPlanIdsForStudy(studyId);
      persistedPlanIds.forEach((planId) => {
        dispatch(addPromotingPlanIdAction(studyId, planId));
      });
    }
  }, [dispatch, studyId]);

  // Clear per-study procedure-run/plan UI state only when the browser page is being exited.
  // This preserves state across internal Benchling tab switches while preventing stale carry-over
  // across full tab/page exits.
  useEffect(() => {
    const clearStudyStateOnPageExit = (): void => {
      dispatch(resetStudyState(studyId));
    };

    window.addEventListener('pagehide', clearStudyStateOnPageExit);
    window.addEventListener('beforeunload', clearStudyStateOnPageExit);

    return () => {
      window.removeEventListener('pagehide', clearStudyStateOnPageExit);
      window.removeEventListener('beforeunload', clearStudyStateOnPageExit);
    };
  }, [dispatch, studyId]);

  // Convert promotingPlanIds array to Set for backwards compatibility with existing code
  const promotingPlanIds = useMemo(() => new Set(reduxPromotingPlanIds), [reduxPromotingPlanIds]);

  const procedureRunResult = useSkippableBenchlingQuery<
    MultiProcedureRunOnlyOverviewQuery,
    MultiProcedureRunOnlyOverviewQueryVariables
  >(MULTI_PROCEDURE_RUN_ONLY_OVERVIEW_QUERY, {
    variables: {id: props.runId!},
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    skip: props.runId == null,
  });
  const procedureRunPlanResult = useSkippableBenchlingQuery<
    MultiProcedureRunPlanOverviewQuery,
    MultiProcedureRunPlanOverviewQueryVariables
  >(MULTI_PROCEDURE_RUN_PLAN_OVERVIEW_QUERY, {
    variables: {id: props.runPlanId!},
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    skip: props.runPlanId == null,
  });
  let fetchedProcedureRun: Extract<
    MultiProcedureRunOnlyOverviewQuery['procedureRun'],
    {__typename: 'ProcedureRunNode'}
  > | null = null;
  let fetchedProcedureRunPlan: ProcedureRunPlanNode | null = null;
  let fetchedTemplateFlowchartVersion: PowAgnosticTemplateFlowchartVersion | null = null;
  let procedureRunPlanIdFromRun: string | null = null;
  // if a user navigates to the procedure run/plan view via Url, we only show the procedure run/plan view if
  // the run/plan ID is valid and if the user has permission to view the run/plan. Otherwise, we navigate to
  // the procedure run/plan listing view
  if (
    procedureRunResult.type === QueryResultType.SUCCESS &&
    procedureRunResult.data &&
    !isInaccessible(procedureRunResult.data.procedureRun)
  ) {
    fetchedProcedureRun = procedureRunResult.data.procedureRun;
    // Extract procedure run plan ID from the procedure run if it exists
    if (
      fetchedProcedureRun.procedureRunPlan != null &&
      !isInaccessible(fetchedProcedureRun.procedureRunPlan)
    ) {
      procedureRunPlanIdFromRun = fetchedProcedureRun.procedureRunPlan.id;
    }
  }
  if (
    procedureRunPlanResult.type === QueryResultType.SUCCESS &&
    procedureRunPlanResult.data &&
    !isInaccessible(procedureRunPlanResult.data.procedureRunPlan)
  ) {
    fetchedProcedureRunPlan = procedureRunPlanResult.data.procedureRunPlan;
    const flowchartVersion =
      getPowAgnosticTemplateFlowchartVersionFromProcedureRunPlan(fetchedProcedureRunPlan);
    if (flowchartVersion != null && !isInaccessible(flowchartVersion)) {
      fetchedTemplateFlowchartVersion = flowchartVersion;
    }
  }
  const selectedProcedureRun = useMemo(() => {
    if (fetchedProcedureRun != null) {
      return {id: fetchedProcedureRun.id, name: fetchedProcedureRun.name};
    }
    return {id: null, name: null};
  }, [fetchedProcedureRun]);

  const selectedProcedureForPlan: ProcedureFromFlowchartVersion | null =
    fetchedTemplateFlowchartVersion != null
      ? getPowAgnosticProcedureFromFlowchartVersion(fetchedTemplateFlowchartVersion)
      : null;
  const selectedProcedureRunPlan = useMemo(() => {
    if (fetchedProcedureRunPlan != null) {
      const name = getProcedureRunPlanName(fetchedProcedureRunPlan);
      return {
        id: fetchedProcedureRunPlan.id,
        name,
        procedureId: selectedProcedureForPlan?.id ?? null,
      };
    }
    return {id: null, name: null, procedureId: null};
  }, [fetchedProcedureRunPlan, selectedProcedureForPlan]);
  assert(
    selectedProcedureRun || selectedProcedureRunPlan,
    'Expected either a procedure run or procedure run plan to be selected.'
  );

  // Sync selected procedure run to Redux when fetched
  useEffect(() => {
    if (selectedProcedureRun.id != null) {
      dispatch(setSelectedProcedureRun(studyId, selectedProcedureRun));
    }
  }, [dispatch, selectedProcedureRun, studyId]);

  // Sync selected procedure run plan to Redux when fetched
  useEffect(() => {
    if (selectedProcedureRunPlan.id != null) {
      dispatch(setSelectedProcedureRunPlan(studyId, selectedProcedureRunPlan));
    }
  }, [dispatch, selectedProcedureRunPlan, studyId]);

  // Callback to set current procedure run tab (dispatches to Redux)
  const setCurrentProcedureRunTab = useCallback(
    (tab: ProcedureRunTab) => {
      dispatch(setCurrentProcedureRunTabAction(studyId, tab));
    },
    [dispatch, studyId]
  );

  // If the user navigates directly to a plan that is actively being promoted, redirect
  // them to the study overview listing page instead of showing the stale plan detail view.
  // We check both Redux state and persisted tasks to handle the case where the component
  // is mounted before Redux state is initialized.
  useEffect(() => {
    if (props.runPlanId != null) {
      const persistedPromotingIds = getPromotingPlanIdsForStudy(studyId);
      if (promotingPlanIds.has(props.runPlanId) || persistedPromotingIds.has(props.runPlanId)) {
        navigateToStudyOverviewTab(studyId);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // If the user navigates to a plan that has already been promoted to a procedure run,
  // redirect them to the resulting procedure run and show an informational toast.
  const procedureRunIdFromPlan =
    fetchedProcedureRunPlan?.procedureRun != null && !isInaccessible(fetchedProcedureRunPlan.procedureRun)
      ? fetchedProcedureRunPlan.procedureRun.id
      : null;
  useEffect(() => {
    if (procedureRunIdFromPlan != null) {
      const label = getProcedureRunUIStringForStudyType(props.study.studySchema.studyType);
      notifications.info(`This plan has already been promoted to a ${label}.`);
      navigateToStudyOverviewTab(props.study.id, procedureRunIdFromPlan);
    }
  }, [procedureRunIdFromPlan, props.study.id, props.study.studySchema.studyType]);

  const [isConfirmPromotionModalOpen, setIsConfirmPromotionModalOpen] = useState(false);

  // Ref for saving plan if in edit mode
  const savePlanIfInEditModeRef = useRef<(() => Promise<void>) | null>(null);
  const [planErrors, setPlanErrors] = useState<Array<ErrorBadgeError>>([]);

  // Callback to set edit mode (dispatches to Redux)
  const setIsInEditMode = useCallback(
    (editMode: boolean) => {
      dispatch(setIsInEditModeAction(studyId, editMode));
    },
    [dispatch, studyId]
  );

  const addPromotingPlanId = useCallback(
    (planId: string) => {
      dispatch(addPromotingPlanIdAction(studyId, planId));
    },
    [dispatch, studyId]
  );

  const removePromotingPlanId = useCallback(
    (planId: string) => {
      dispatch(removePromotingPlanIdAction(studyId, planId));
    },
    [dispatch, studyId]
  );

  const handlePromotePlan = useCallback(() => {
    const planId = selectedProcedureRunPlan.id;
    if (planId == null) {
      return;
    }

    const procedureRunLabel = getProcedureRunUIStringForStudyType(props.study.studySchema.studyType);
    const createPromotionTrayTask = (): string =>
      TrayTaskActions.createTrayTask({
        heading: (
          <ToastMessage
            body={<>Creating {procedureRunLabel}...</>}
            progress={TrayTaskConstants.INDETERMINATE_PROGRESS}
          />
        ),
        // Hide "native" percentage, since ToastMessage has a better UI when totalTimeMs is provided.
        progress: TrayTaskConstants.HIDE_PROGRESS,
      });

    const refreshAfterPromotion = (creates: Array<{id: string; folderId: string}>): Promise<void> =>
      onSuccessAfterExperimentCreate(props.study.id, {creates}, props.study.studySchema.studyType);

    const navigateToCreatedRun = (creates: Array<{id: string}>): void => {
      const createdRunId = creates[0]?.id ?? undefined;
      navigateToStudyOverviewTab(props.study.id, createdRunId);
    };

    const promotePlan = async (): Promise<void> => {
      // If the plan is in edit mode, save it first before promoting
      if (isInEditMode && savePlanIfInEditModeRef.current != null) {
        await savePlanIfInEditModeRef.current();
      }

      addPromotingPlanId(planId);
      const baseUnknownErrorAction = getExperimentUnknownErrorAction(
        props.study.id,
        props.study.studySchema.studyType
      );
      const onUnknownError = {
        ...baseUnknownErrorAction,
        onShowErrorToast: () => {
          removePromotingPlanId(planId);
        },
      };
      let shouldClearPromotingId = true;

      // Navigate to study overview immediately so user sees the listing with the greyed out row
      navigateToStudyOverviewTab(props.study.id);

      try {
        const response = await promoteProcedureRunPlans([planId], {supportsAsync: true});
        if (response.async) {
          shouldClearPromotingId = false;
          showExperimentCreationTrayTask(
            response.data.taskId,
            response.data.status_url,
            response.taskMeta,
            [
              {
                studyId: props.study.id,
                studyType: props.study.studySchema.studyType,
                procedureRunPlanId: planId,
              },
            ],
            {
              onSuccess: (result) => {
                refreshAfterPromotion(result.creates);
                removePromotingPlanId(planId);
              },
              onOpen: (result) => {
                navigateToCreatedRun(result.creates);
              },
              onUnknownError,
              onRetry: () => {
                removePromotingPlanId(planId);
                promotePlan();
              },
            },
            response.data.timingMeta
          );
          return;
        }
        refreshAfterPromotion(response.data.creates);
        const trayTaskId = createPromotionTrayTask();
        TrayTaskActions.updateTrayTask(trayTaskId, {
          progress: TrayTaskConstants.SUCCESS_PROGRESS,
          heading: <ToastMessage body={<>Created {procedureRunLabel} successfully.</>} progress={100} />,
          onHeadingClick: () => {
            navigateToCreatedRun(response.data.creates);
          },
        });
      } catch (error) {
        const trayTaskId = createPromotionTrayTask();
        TrayTaskActions.updateTrayTask(trayTaskId, {
          progress: TrayTaskConstants.FAILURE_PROGRESS,
          error: (
            <ToastMessage
              header={`Creating ${procedureRunLabel} failed.`}
              body={notifications.getFriendlyError(error) ?? 'An error occurred.'}
            />
          ),
          actions: [
            {
              actionText: 'Retry...',
              onActionClick: () => {
                promotePlan();
              },
            },
          ],
        });
        throw error;
      } finally {
        if (shouldClearPromotingId) {
          removePromotingPlanId(planId);
        }
      }
    };

    promotePlan().catch(() => undefined);
  }, [
    selectedProcedureRunPlan.id,
    addPromotingPlanId,
    removePromotingPlanId,
    props.study.id,
    props.study.studySchema.studyType,
    isInEditMode,
  ]);

  const openConfirmPromotionModal = useCallback(() => {
    setIsConfirmPromotionModalOpen(true);
  }, []);

  const closeConfirmPromotionModal = useCallback(() => {
    setIsConfirmPromotionModalOpen(false);
  }, []);

  const handleConfirmPromotion = useCallback(() => {
    closeConfirmPromotionModal();
    handlePromotePlan();
  }, [closeConfirmPromotionModal, handlePromotePlan]);

  const setSavePlanIfInEditMode = useCallback((fn: (() => Promise<void>) | null) => {
    savePlanIfInEditModeRef.current = fn;
  }, []);

  const studyOverviewContextValue = useMemo(
    () => ({
      selectedProcedureRun,
      selectedProcedureRunPlan,
      currentProcedureRunTab,
      setCurrentProcedureRunTab,
      promotingPlanIds,
      addPromotingPlanId,
      removePromotingPlanId,
      isInEditMode,
      setIsInEditMode,
      setSavePlanIfInEditMode,
      planErrors,
      setPlanErrors,
    }),
    [
      currentProcedureRunTab,
      selectedProcedureRun,
      selectedProcedureRunPlan,
      setCurrentProcedureRunTab,
      promotingPlanIds,
      addPromotingPlanId,
      removePromotingPlanId,
      isInEditMode,
      setIsInEditMode,
      setSavePlanIfInEditMode,
      planErrors,
    ]
  );

  if (
    procedureRunResult.type === QueryResultType.PENDING ||
    procedureRunPlanResult.type === QueryResultType.PENDING
  ) {
    return <CenteredSpinner />;
  }
  return (
    <StudyOverviewContext.Provider value={studyOverviewContextValue}>
      {isConfirmPromotionModalOpen && selectedProcedureRunPlan.procedureId != null && (
        <ConfirmPromotionModal
          studyType={props.study.studySchema.studyType}
          procedureId={selectedProcedureRunPlan.procedureId}
          onRequestClose={closeConfirmPromotionModal}
          onRequestSubmit={() => {
            handleConfirmPromotion();
          }}
        />
      )}
      <StudyExecutionHeader
        {...props}
        dataTestAttrs={{component: 'MultiProcedureRunOverviewTabContents', element: 'overview'}}
        executionContainerStyling={executionContainer}
        headerSectionContainerStyling={headerSectionContainerStyling}
        title={(selectedProcedureRun.name || selectedProcedureRunPlan.name) ?? undefined}
        headerBreadcrumb={studyOverviewCrumbs({
          selectedProcedureRun,
          selectedProcedureRunPlan,
          studyName: props.study.name,
          setCurrentProcedureRunTab,
          studyId: props.study.id,
        })}
        procedureRunPlanId={selectedProcedureRunPlan.id ?? undefined}
        procedureRunId={selectedProcedureRun.id ?? undefined}
        headerButtons={{
          buttons:
            selectedProcedureRun.id != null || selectedProcedureRunPlan.id != null ? (
              getProcedureRunOrPlanViewButtons({
                study: props.study,
                selectedProcedureRunId: selectedProcedureRun.id ?? undefined,
                selectedProcedureRunPlanId:
                  selectedProcedureRunPlan.id ?? procedureRunPlanIdFromRun ?? undefined,
                planOrRunName: (selectedProcedureRun.name || selectedProcedureRunPlan.name) ?? '',
                onLinksSaved: props.refetch,
                onOpenPromotePlanConfirmation: openConfirmPromotionModal,
                planErrors,
                isInEditMode,
                isProcedureRunArchived: fetchedProcedureRun?.archiveData.archived ?? false,
              })
            ) : (
              <>
                <AnalyzeMultipleProcedureRunsButton
                  studyId={props.study.id}
                  studyName={props.study.name}
                  studyHumanId={props.study.humanId}
                  studyType={props.study.studySchema.studyType}
                  isStudyArchived={props.study.archiveRecord != null}
                  procedureRunIds={accessibleObjs(props.study.procedureRuns).map(
                    (procedureRun) => procedureRun.id
                  )}
                  procedureType={getProcedureTypeForStudyType(props.study.studySchema.studyType)}
                  actionOrigin={ActionOrigin.STUDY_OVERVIEW_PAGE}
                />
                <SetStudyPhaseDropdown
                  study={{
                    id: props.study.id,
                    phase: props.study.phase,
                    studyType: props.study.studySchema.studyType,
                  }}
                  refetch={props.refetch}
                  isDisabled={!props.isEditable}
                  requiresApproval={props.isDesignApprovalRequiredToProceed}
                  buttonSize={ButtonSize.MEDIUM}
                  allowReturnToDesignPhase={props.allowReturnToDesignPhase}
                />
                <AddProcedureRunButton
                  studyId={props.study.id}
                  studyType={props.study.studySchema.studyType}
                />
              </>
            ),
          buttonsStyling: buttonStyling,
        }}
        {...procedureRunViewerTabsProps({
          currentProcedureRunTab,
          setCurrentProcedureRunTab,
          selectedProcedureRunId: selectedProcedureRun?.id ?? null,
          selectedProcedureRunPlanId: selectedProcedureRunPlan?.id ?? null,
        })}
      />
    </StudyOverviewContext.Provider>
  );
}

const styles = StyleSheet.create({
  breadcrumbsFont: {
    fontSize: 13,
    color: Colors.GRAY_DARKER,
  },
  headerDivider: {
    borderBottom: `1.5px solid ${Colors.GRAY_LIGHT}`,
  },
  tab: {
    ...FONT_PROXIMA_12,
  },
  tabContainer: {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    color: Colors.GRAY_DARKER,
    marginTop: 18,
  },
  tabList: {
    marginLeft: '12px',
  },
  tabPanel: {
    marginTop: 0,
  },
});

const executionContainer: React.CSSProperties = {
  flex: '1 0',
};

const buttonStyling: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: 8,
  marginTop: -35,
};

const headerSectionContainerStyling: React.CSSProperties = {
  margin: '0 12px 12px',
};
